class LukeRelatedBatches extends HTMLElement {
  connectedCallback() {
    queueMicrotask(() => this.initialize());
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.nextBatch);
    this.controller?.abort();
  }

  initialize() {
    this.button?.removeEventListener('click', this.nextBatch);
    this.button = this.querySelector('[data-related-next]');
    this.status = this.querySelector('[data-related-status]');
    this.grid = this.querySelector('.resource-list--grid');
    if (!this.button || !this.status || !this.grid) return;
    this.seen = new Set([this.dataset.currentProduct]);
    this.cards = [...this.grid.querySelectorAll(':scope > [data-related-product-id]')].filter((card) => {
      const id = card.dataset.relatedProductId;
      if (!id || this.seen.has(id)) { card.hidden = true; return false; }
      this.seen.add(id);
      return true;
    });
    this.size = Math.max(1, Number(this.dataset.batchSize) || 4);
    this.offset = 0;
    this.busy = false;
    this.nextPage = this.dataset.collectionUrl && this.dataset.filterType ? 1 : null;
    this.renderBatch();
    this.querySelector('.luke-related-batches__controls').hidden = !this.cards.length && !this.nextPage;
    this.button.addEventListener('click', this.nextBatch);
    this.needsInitialFill = this.cards.length < this.size && this.nextPage;
    if (this.needsInitialFill) this.fillInitialBatch();
  }

  async fetchPage() {
    const page = this.nextPage;
    const url = new URL(this.dataset.collectionUrl, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid collection origin');
    url.searchParams.set('section_id', this.dataset.sectionId);
    url.searchParams.set('page', String(page));
    if (this.dataset.themeId) url.searchParams.set('preview_theme_id', this.dataset.themeId);
    this.controller = new AbortController();
    const timeout = setTimeout(() => this.controller?.abort(), 12000);
    let response;
    try {
      response = await fetch(url, { signal: this.controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Could not load picks');
      const html = await response.text();
      if (!this.isConnected) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const pool = doc.querySelector('[data-related-pool]');
      if (!pool) throw new Error('Missing category response');
      const expectedHandle = new URL(this.dataset.collectionUrl, location.origin).pathname.split('/').filter(Boolean).pop();
      if (pool.dataset.poolHandle !== expectedHandle) throw new Error('Unexpected category response');
      const next = Number(pool.dataset.nextPage) || null;
      if (next && next <= page) throw new Error('Invalid pagination');
      const additions = [];
      for (const card of pool.querySelectorAll('[data-related-product-id]')) {
        const id = card.dataset.relatedProductId;
        const types = JSON.parse(card.dataset.relatedTypes || 'null');
        if (!id || this.seen.has(id) || !Array.isArray(types) || !types.includes(this.dataset.filterType)) continue;
        const ids = new Map();
        for (const element of card.querySelectorAll('[id]')) {
          ids.set(element.id, `${element.id}-related-${id}`);
        }
        for (const element of card.querySelectorAll('*')) {
          for (const attribute of [...element.attributes]) {
            const value = attribute.value.split(' ').map(token => ids.get(token) || (token.startsWith('#') && ids.has(token.slice(1)) ? `#${ids.get(token.slice(1))}` : token)).join(' ');
            if (value !== attribute.value) element.setAttribute(attribute.name, value);
          }
        }
        card.hidden = true;
        this.seen.add(id);
        additions.push(card);
      }
      this.grid.append(...additions);
      this.cards.push(...additions);
      this.nextPage = next;
    } finally {
      clearTimeout(timeout);
      this.controller = null;
    }
  }

  async ensureCards(target) {
    while (this.cards.length < target && this.nextPage && this.isConnected) await this.fetchPage();
  }

  setBusy(busy) {
    this.busy = busy;
    this.button.disabled = busy;
    if (busy) {
      this.button.textContent = this.dataset.loadingLabel;
      this.grid.setAttribute('aria-busy', 'true');
    } else this.grid.removeAttribute('aria-busy');
  }

  async fillInitialBatch() {
    this.setBusy(true);
    try {
      await this.ensureCards(this.size);
      if (this.isConnected) { this.needsInitialFill = false; this.renderBatch(); }
    } catch {
      if (this.isConnected) { this.renderBatch(); this.status.textContent = this.dataset.errorLabel; }
    } finally {
      this.busy = false;
      this.grid.removeAttribute('aria-busy');
    }
  }

  nextBatch = async () => {
    if (this.busy || (!this.nextPage && this.offset + this.size >= this.cards.length)) return;
    if (this.needsInitialFill) return this.fillInitialBatch();
    const previousOffset = this.offset;
    this.setBusy(true);
    try {
      const nextOffset = this.cards.length ? this.offset + this.size : 0;
      await this.ensureCards(nextOffset + this.size);
      if (!this.isConnected) return;
      if (nextOffset < this.cards.length) this.offset = nextOffset;
      this.renderBatch();
      this.grid.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    } catch {
      if (this.isConnected) {
        this.offset = previousOffset;
        this.renderBatch();
        this.status.textContent = this.dataset.errorLabel;
      }
    } finally {
      this.busy = false;
      this.grid.removeAttribute('aria-busy');
    }
  };

  renderBatch() {
    const end = Math.min(this.offset + this.size, this.cards.length);
    this.cards.forEach((card, index) => { card.hidden = index < this.offset || index >= end; });
    const finished = !this.nextPage && end >= this.cards.length;
    this.button.disabled = finished;
    this.button.textContent = finished ? this.dataset.endLabel : this.dataset.nextLabel;
    const label = this.nextPage ? this.dataset.openStatus : this.dataset.statusLabel;
    const range = label.replace('{start}', String(this.cards.length ? this.offset + 1 : 0)).replace('{end}', String(end)).replace('{total}', String(this.cards.length));
    this.status.textContent = finished ? `${range} ${this.dataset.endStatus}` : range;
  }
}

if (!customElements.get('luke-related-batches')) {
  customElements.define('luke-related-batches', LukeRelatedBatches);
}
